import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="min-h-screen bg-surface flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="text-6xl">!</div>
            <h1 className="text-2xl font-bold text-gray-100">Something went wrong</h1>
            <p className="text-gray-400 text-sm">
              An unexpected error occurred. Please try again.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-4 p-3 bg-surface-card border border-red-500/20 rounded-lg text-left text-xs text-red-400 overflow-auto max-h-40">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleReset}
                className="px-5 py-2.5 bg-brand-yellow text-surface font-medium rounded-lg hover:bg-brand-gold transition-colors text-sm"
              >
                Try again
              </button>
              <a
                href="/"
                onClick={this.handleReset}
                className="px-5 py-2.5 bg-surface-card border border-white/10 text-gray-300 font-medium rounded-lg hover:bg-surface-hover transition-colors text-sm"
              >
                Go home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
